import { useState, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShellBar, ShellBarItem, SideNavigation, SideNavigationItem, SideNavigationSubItem, Avatar, FlexBox, Popover, Title, Text, Button } from '@ui5/webcomponents-react';
import type { ScreenSummary } from '@b1links/core';
import { useAuth } from '../store/auth';
import { api } from '../api/client';
import { guideUrl } from '../platform';

const byOrder = (a: ScreenSummary, b: ScreenSummary) => (a.menu?.order ?? 999) - (b.menu?.order ?? 999) || a.title.localeCompare(b.title);

/** App frame: Fiori shell bar + 3-level side navigation (module → sub-group → screen) built from screen definitions. */
export function Shell({ children }: { children: ReactNode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { principal, logout, can } = useAuth();
  const qc = useQueryClient();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 700);
  const [collapsed, setCollapsed] = useState(false); // desktop: start expanded so all module folders are visible (toggle to icon rail with the menu button)
  const [open, setOpen] = useState(false);                              // mobile: drawer open
  const [profileOpen, setProfileOpen] = useState(false);                // profile popover
  const [profileOpener, setProfileOpener] = useState<HTMLElement | undefined>();
  const screens = useQuery({ queryKey: ['screens'], queryFn: api.screens, staleTime: 60_000 });
  // Which portal this deployment is. The same build serves /B1Links, /B1Budget and /B1Conso, so the shell
  // has to be told its own name rather than assume it; it rarely changes, hence the long stale time.
  const portal = useQuery({ queryKey: ['portal'], queryFn: api.portal, staleTime: Infinity });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onLogout = async () => { try { await api.logout(); } catch { /* ignore */ } logout(); qc.clear(); };
  const toggleMenu = () => (isMobile ? setOpen(o => !o) : setCollapsed(c => !c));
  // On mobile, picking a screen closes the drawer so the content gets the full width.
  const go = (e: CustomEvent<{ item: HTMLElement }>) => { const p = e.detail.item.dataset.path; if (p) { nav(p); if (isMobile) setOpen(false); } };
  const isActive = (p: string) => loc.pathname === p || loc.pathname.startsWith(p + '/');

  // Group into modules, then sub-groups, preserving order.
  const visible = (screens.data ?? []).filter(s => s.access || can(`${s.entity}.View`)).sort(byOrder);
  const modules = new Map<string, ScreenSummary[]>();
  for (const s of visible) { const g = s.menu?.group ?? 'Screens'; (modules.get(g) ?? modules.set(g, []).get(g)!).push(s); }

  return (
    <FlexBox direction="Column" style={{ height: '100%' }}>
      <ShellBar
        primaryTitle={portal.data?.name ?? 'B1Links'}
        secondaryTitle={isMobile ? (principal?.companyDb ?? '') : `${principal?.displayName} · ${principal?.companyDb} · ${principal?.mode === 'sap' ? 'SAP user' : 'App user'} · ${principal?.channel}`}
        onLogoClick={() => nav('/')}
        onProfileClick={(e) => { setProfileOpener((e.detail as { targetRef?: HTMLElement } | undefined)?.targetRef); setProfileOpen(true); }}
        profile={<Avatar initials={(principal?.displayName ?? '?').slice(0, 2).toUpperCase()} size="XS" />}
        startButton={<ShellBarItem icon="menu2" text="Menu" onClick={toggleMenu} />}
      >
        <ShellBarItem icon="log" text="Sign out" onClick={onLogout} />
      </ShellBar>
      <Popover open={profileOpen} opener={profileOpener} placement="Bottom" onClose={() => setProfileOpen(false)}>
        <div style={{ padding: '0.5rem 0.25rem', minWidth: '15rem' }}>
          <FlexBox alignItems="Center" style={{ gap: '0.6rem', marginBottom: '0.6rem' }}>
            <Avatar initials={(principal?.displayName ?? '?').slice(0, 2).toUpperCase()} size="S" />
            <div style={{ minWidth: 0 }}>
              <Title level="H6" style={{ margin: 0 }}>{principal?.displayName}</Title>
              <Text style={{ color: 'var(--sapNeutralTextColor)' }}>{principal?.user}</Text>
            </div>
          </FlexBox>
          <div style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', lineHeight: 1.7 }}>
            <div>Company: {principal?.companyDb}</div>
            <div>Type: {principal?.mode === 'sap' ? 'SAP user' : 'App user'} · {principal?.channel}</div>
            {principal?.roles?.length ? <div>Roles: {principal.roles.join(', ')}</div> : null}
          </div>
          <FlexBox wrap="Wrap" style={{ gap: '0.4rem', marginTop: '0.8rem' }}>
            <Button icon="sys-help" onClick={() => { setProfileOpen(false); window.open(guideUrl(), '_blank'); }}>User Guide</Button>
            <Button icon="action-settings" onClick={() => { setProfileOpen(false); nav('/settings'); }}>Settings</Button>
            <Button design="Emphasized" icon="log" onClick={() => { setProfileOpen(false); onLogout(); }}>Sign out</Button>
          </FlexBox>
        </div>
      </Popover>
      <div style={{ position: 'relative', display: 'flex', flex: 1, minHeight: 0 }}>
        {isMobile && open && <div onClick={() => setOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10 }} />}
        <SideNavigation
          collapsed={isMobile ? false : collapsed}
          onSelectionChange={go as never}
          style={isMobile
            ? { position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 11, width: '17rem', maxWidth: '85vw', background: 'var(--sapList_Background, #fff)', boxShadow: open ? '0 0 1rem rgba(0,0,0,0.35)' : 'none', transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.2s ease' }
            : { height: '100%' }}>
          <SideNavigationItem text="Home" icon="home" data-path="/" selected={loc.pathname === '/'} />
          {/* Each module is a collapsible folder (shows its name; expands to reveal its screens). */}
          {[...modules.entries()].map(([module, list]) => (
            <SideNavigationItem key={module} text={module} icon="folder-blank" expanded={list.some(s => isActive(`/s/${s.id}`))}>
              {list.map(s => (
                <SideNavigationSubItem key={s.id} text={s.menu?.subgroup ? `${s.menu.subgroup} · ${s.title}` : s.title} icon={s.icon} data-path={`/s/${s.id}`} selected={isActive(`/s/${s.id}`)} />
              ))}
            </SideNavigationItem>
          ))}
          {can('Admin.Users') && <SideNavigationItem text="Configuration" icon="settings" data-path="/admin" selected={isActive('/admin')} />}
          <SideNavigationItem text="Settings" icon="action-settings" data-path="/settings" selected={loc.pathname === '/settings'} />
          <SideNavigationItem text="User Guide" icon="sys-help" href={guideUrl()} target="_blank" />
        </SideNavigation>
        <main style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: isMobile ? '0.5rem' : '0.75rem 1rem' }}>{children}</main>
      </div>
    </FlexBox>
  );
}
